import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';

import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./report-summary.i18n.json'));

import type { Action, Dispatch, ThunkAction } from 'types';
import type {
  KpiSettingValues,
  ReportingSummary,
  ReportingSummaryState
} from 'modules/reporting/types';

const { REPORTING } = API.END_POINTS;

const PROCESSING = 'reporting-v2/PROCESSING';
const LOADED = 'reporting-v2/LOADED';
const RESET = 'reporting-v2/RESET';
const LOAD_KPI_SETTING = 'reporting-v2/LOAD_KPI_SETTING';

const DEFAULT_KPI_SETTING_VALUE = 'total_of_snippets';

const initialState: ReportingSummaryState = {
  summary: {},
  isLoading: true,
  kpiSettingValue: DEFAULT_KPI_SETTING_VALUE
};

const setStatus = (status: boolean): Action => ({
  type: PROCESSING,

  payload: {
    status
  }
});

const summaryLoaded = (summary: ReportingSummary): Action => ({
  type: LOADED,

  payload: {
    summary
  }
});

const kpiSettingLoaded = (kpiSettingValue: KpiSettingValues): Action => ({
  type: LOAD_KPI_SETTING,

  payload: {
    kpiSettingValue
  }
});

export const resetSummary = (): Action => ({
  type: RESET
});

export const fetchSummary = (campaignId: number): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  try {
    // TODO Remove eslint and flowignore when BE is working properly
    const url = REPORTING.getReportSummary(campaignId);
    const response = await get(url, {}, { suppressGlobalNotification: true });
    // eslint-disable-next-line no-unused-vars
    const summary = response.data || {};

    dispatch(summaryLoaded(summary));
    dispatch(setStatus(false));
  } catch (error) {
    logError(error);
  } finally {
    dispatch(setStatus(false));
  }
};

export const fetchKpiSettings = (campaignId: number): ThunkAction => async (dispatch: Dispatch) => {
  const url = REPORTING.fetchKpiSettings(campaignId);

  dispatch(showLoading(url));

  try {
    const response = await get(url);
    const setting = response.data || {};
    const kpiSettingValue = setting.instagramStoryKpi?.snippetType || DEFAULT_KPI_SETTING_VALUE;

    dispatch(hideLoading(url));

    return dispatch(kpiSettingLoaded(kpiSettingValue));
  } catch (error) {
    logError(error);

    return dispatch(danger(__t('report_summary_reducer.kpi_setting_fetch_error')));
  } finally {
    dispatch(hideLoading(false));
  }
};

export const saveKpiSettings = (
  campaignId: number,
  kpiSettingValue: KpiSettingValues,
  saveSettingForUpcomingCamapign: boolean
): ThunkAction => async (dispatch: Dispatch) => {
  const url = REPORTING.saveKpiSettings(campaignId);

  dispatch(showLoading(url));

  try {
    const response = await post(url, {
      data: {
        campaignId,
        instagramStoryKpi: {
          snippetType: kpiSettingValue,
          isDefault: saveSettingForUpcomingCamapign
        }
      }
    });

    dispatch(hideLoading(url));
    dispatch(kpiSettingLoaded(kpiSettingValue));
    dispatch(success(__t('report_summary_reducer.kpi_saved_success_message')));

    return response;
  } catch (error) {
    logError(error);

    return dispatch(danger(__t('report_summary_reducer.kpi_saving_error')));
  } finally {
    dispatch(hideLoading(false));
  }
};

const handleProcessing = (state: ReportingSummaryState, { status }): ReportingSummaryState => ({
  ...state,
  isLoading: status
});

const handleLoaded = (state: ReportingSummaryState, { summary }): ReportingSummaryState => ({
  ...state,
  summary,
  isLoading: false
});

const handleKpiSettingValue = (state: ReportingSummaryState, { kpiSettingValue }): ReportingSummaryState => ({
  ...state,
  kpiSettingValue
});

export default createReducer<ReportingSummaryState, any>('reportingV2', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [PROCESSING]: handleProcessing,
  [LOADED]: handleLoaded,
  [LOAD_KPI_SETTING]: handleKpiSettingValue
});
